import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  increment,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Comment, CommentFormData, CommentReaction } from '../types/comment';
import { systemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { NotificationService } from './notificationService';
import { mentionService } from './mentionService';
import { mentionParser } from '../utils/mention-parser';

/**
 * ProfileCommentService
 * Manages threaded comments on person profiles with reactions and @mentions
 *
 * Implements comprehensive 10-step service integration pattern:
 * 1. Get SystemConfig
 * 2. Check feature flags & permissions
 * 3. Validate against config limits
 * 4. Check threading depth limits
 * 5. No approval workflow (comments are instant)
 * 6. Perform Firebase operation
 * 7. Log to ChangeTrackingService
 * 8. Log to AuditLogService
 * 9. Send email notification (for @mentions and replies)
 * 10. Return result
 */
export class ProfileCommentService {
  private static readonly COLLECTION_NAME = 'profileComments';

  /**
   * Create a new comment or reply
   */
  static async createComment(
    formData: CommentFormData,
    userId: string,
    userName: string,
    userRole: string,
    personId: string,
    personName: string
  ): Promise<Comment> {
    try {
      // STEP 1: Get SystemConfig
      const config = await systemConfigService.getConfig();

      // STEP 2: Check feature flags & permissions
      if (!config.features.enableCommentsTab) {
        throw new Error('Comments feature is currently disabled');
      }

      // Check role permissions (Contributor+)
      const allowedRoles = ['contributor', 'editor', 'admin'];
      if (!allowedRoles.includes(userRole)) {
        throw new Error('You do not have permission to post comments');
      }

      // Check if mentions are enabled
      if (formData.mentions && formData.mentions.length > 0 && !config.content.enableCommentMentions) {
        throw new Error('Comment mentions are currently disabled');
      }

      // STEP 3: Validate content
      if (!formData.content || formData.content.trim().length === 0) {
        throw new Error('Comment content cannot be empty');
      }

      // STEP 4: Check threading depth limits
      let threadDepth = 0;
      if (formData.parentId) {
        if (!config.content.enableCommentThreading) {
          throw new Error('Comment threading is currently disabled');
        }

        threadDepth = await this.calculateThreadDepth(formData.parentId);
        const maxDepth = config.content.maxCommentThreadDepth;

        if (threadDepth >= maxDepth) {
          throw new Error(`Maximum comment thread depth of ${maxDepth} reached`);
        }
      }

      // STEP 5: No approval workflow - comments are instant

      // STEP 6: Perform Firebase operation
      const now = new Date();

      // Build comment data with only defined fields
      const commentData: any = {
        // Association
        personId,
        personName,

        // Target
        targetType: 'person',
        targetId: personId,

        // Content
        content: formData.content,
        attachments: [],

        // Threading
        threadDepth,

        // Mentions
        mentions: formData.mentions || [],

        // Authorship
        authorId: userId,
        authorName: userName,

        // Engagement
        likes: [],
        likeCount: 0,
        replyCount: 0,
        reactions: [],

        // Moderation
        isApproved: true, // Profile comments are auto-approved

        // Edit tracking
        isEdited: false,
        canEdit: true, // Will be checked based on time window

        // Metadata
        createdBy: userId,
        createdAt: now,
        updatedAt: now,

        // Soft delete
        isDeleted: false,
      };

      // Add optional fields only if they have values
      if (formData.parentId) {
        commentData.parentId = formData.parentId;
      }
      if (formData.replyToUserId) {
        commentData.replyToUserId = formData.replyToUserId;
      }
      if (formData.replyToUserName) {
        commentData.replyToUserName = formData.replyToUserName;
      }
      if (formData.authorPhotoURL) {
        commentData.authorPhotoURL = formData.authorPhotoURL;
      }

      // Remove undefined fields before saving to Firestore (Firestore doesn't accept undefined)
      const cleanedData = this.removeUndefinedFields(commentData);

      // Save to Firestore with serverTimestamp
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await updateDoc(docRef, { id: docRef.id });

      const comment: Comment = {
        ...commentData,
        id: docRef.id,
      };

      // Update reply count on parent if this is a reply
      if (formData.parentId) {
        await this.incrementReplyCount(formData.parentId);
      }

      // STEP 7: Log to ChangeTrackingService
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: comment.id,
          changeType: 'create',
          beforeState: null,
          afterState: comment,
          changedFields: Object.keys(commentData),
          reason: formData.parentId ? 'Reply posted' : 'Comment posted',
        },
        userId,
        userName,
        false
      );

      // STEP 8: Log to AuditLogService
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'comment',
        contentId: comment.id,
        details: {
          personId,
          personName,
          userName,
          isReply: !!formData.parentId,
          hasMentions: (formData.mentions?.length || 0) > 0,
          mentionedUsers: formData.mentions || [],
        },
      });

      // STEP 9: Send notifications (in-app, non-blocking)
      // Send mention notifications using the MentionService
      if (formData.mentions && formData.mentions.length > 0) {
        await mentionService.processMentions(formData.mentions, {
          contentType: 'comment',
          contentId: comment.id,
          authorId: userId,
          authorName: userName,
          contentPreview: mentionParser.getContentPreview(formData.content, 100),
          targetType: 'person',
          targetId: personId,
        }).catch(err => console.error('Failed to process mention notifications:', err));
      }

      // Notify parent comment author about reply
      if (formData.replyToUserId && formData.replyToUserId !== userId) {
        NotificationService.createNotification({
          userId: formData.replyToUserId,
          type: 'comment_reply',
          actionUrl: `/people/${personId}/comments?comment=${comment.id}`,
          data: {
            commentId: comment.id,
            personId,
            personName,
            repliedBy: userName,
            commentPreview: formData.content.substring(0, 100),
          },
        }).catch(err => console.error('Failed to create reply notification:', err));
      }

      // STEP 10: Return result
      return comment;

    } catch (error: any) {
      console.error('Error creating comment:', error);
      throw new Error(`Failed to create comment: ${error.message}`);
    }
  }

  /**
   * Get all comments for a person (top-level only by default)
   */
  static async getCommentsByPerson(
    personId: string,
    includeReplies: boolean = false
  ): Promise<Comment[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('personId', '==', personId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
      ];

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const querySnapshot = await getDocs(q);

      const comments: Comment[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Filter replies in memory (can't query on parentId because it may not exist in the document)
        if (!includeReplies && data.parentId) {
          return; // Skip replies if not including them
        }

        comments.push({
          ...data,
          id: doc.id,
          createdAt: this.timestampToDate(data.createdAt),
          updatedAt: this.timestampToDate(data.updatedAt),
          editedAt: data.editedAt ? this.timestampToDate(data.editedAt) : undefined,
          deletedAt: data.deletedAt ? this.timestampToDate(data.deletedAt) : undefined,
          canEdit: this.canEditComment(data.createdAt, data.authorId),
        } as Comment);
      });

      return comments;
    } catch (error: any) {
      console.error('Error getting comments:', error);
      throw new Error(`Failed to get comments: ${error.message}`);
    }
  }

  /**
   * Get replies to a specific comment
   */
  static async getReplies(parentId: string): Promise<Comment[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('parentId', '==', parentId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'asc'),
      ];

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const querySnapshot = await getDocs(q);

      const replies: Comment[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        replies.push({
          ...data,
          id: doc.id,
          createdAt: this.timestampToDate(data.createdAt),
          updatedAt: this.timestampToDate(data.updatedAt),
          editedAt: data.editedAt ? this.timestampToDate(data.editedAt) : undefined,
          deletedAt: data.deletedAt ? this.timestampToDate(data.deletedAt) : undefined,
          canEdit: this.canEditComment(data.createdAt, data.authorId),
        } as Comment);
      });

      return replies;
    } catch (error: any) {
      console.error('Error getting replies:', error);
      throw new Error(`Failed to get replies: ${error.message}`);
    }
  }

  /**
   * Get a specific comment by ID
   */
  static async getCommentById(commentId: string): Promise<Comment | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: this.timestampToDate(data.createdAt),
        updatedAt: this.timestampToDate(data.updatedAt),
        editedAt: data.editedAt ? this.timestampToDate(data.editedAt) : undefined,
        deletedAt: data.deletedAt ? this.timestampToDate(data.deletedAt) : undefined,
        canEdit: this.canEditComment(data.createdAt, data.authorId),
      } as Comment;
    } catch (error: any) {
      console.error('Error getting comment:', error);
      throw new Error(`Failed to get comment: ${error.message}`);
    }
  }

  /**
   * Update a comment (within edit window)
   */
  static async updateComment(
    commentId: string,
    content: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const existingComment = await this.getCommentById(commentId);
      if (!existingComment) {
        throw new Error('Comment not found');
      }

      // Check permissions (owner within edit window or admin)
      const isAdmin = userRole === 'admin';
      const isOwner = existingComment.authorId === userId;

      if (!isAdmin && !isOwner) {
        throw new Error('You do not have permission to edit this comment');
      }

      // Check edit window (non-admins only)
      if (!isAdmin) {
        const config = await systemConfigService.getConfig();
        const editWindowMinutes = config.content.commentEditWindowMinutes;
        const now = new Date();
        const createdAt = new Date(existingComment.createdAt);
        const minutesSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60);

        if (minutesSinceCreation > editWindowMinutes) {
          throw new Error(`Edit window of ${editWindowMinutes} minutes has expired`);
        }
      }

      // Validate content
      if (!content || content.trim().length === 0) {
        throw new Error('Comment content cannot be empty');
      }

      // Update comment
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await updateDoc(docRef, {
        content,
        isEdited: true,
        editedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: commentId,
          changeType: 'update',
          beforeState: existingComment,
          afterState: { ...existingComment, content, isEdited: true },
          changedFields: ['content'],
          reason: 'Comment edited',
        },
        userId,
        userName,
        isAdmin
      );

    } catch (error: any) {
      console.error('Error updating comment:', error);
      throw new Error(`Failed to update comment: ${error.message}`);
    }
  }

  /**
   * Like/unlike a comment
   */
  static async toggleLike(
    commentId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const existingComment = await this.getCommentById(commentId);
      if (!existingComment) {
        throw new Error('Comment not found');
      }

      const likes = existingComment.likes || [];
      const hasLiked = likes.includes(userId);

      const docRef = doc(db, this.COLLECTION_NAME, commentId);

      if (hasLiked) {
        // Unlike
        await updateDoc(docRef, {
          likes: likes.filter(id => id !== userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      } else {
        // Like
        await updateDoc(docRef, {
          likes: [...likes, userId],
          likeCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      }

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: commentId,
          changeType: 'update',
          changedFields: ['likes', 'likeCount'],
          reason: hasLiked ? 'Comment unliked' : 'Comment liked',
        },
        userId,
        userName,
        false
      );

    } catch (error: any) {
      console.error('Error toggling like:', error);
      throw new Error(`Failed to toggle like: ${error.message}`);
    }
  }

  /**
   * Add emoji reaction to a comment
   */
  static async addReaction(
    commentId: string,
    emoji: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const config = await systemConfigService.getConfig();
      if (!config.content.enableCommentReactions) {
        throw new Error('Comment reactions are currently disabled');
      }

      const existingComment = await this.getCommentById(commentId);
      if (!existingComment) {
        throw new Error('Comment not found');
      }

      const reactions = existingComment.reactions || [];

      // Check if user already reacted with this emoji
      const existingReaction = reactions.find(
        r => r.userId === userId && r.emoji === emoji
      );

      if (existingReaction) {
        throw new Error('You have already reacted with this emoji');
      }

      // Add new reaction
      const newReaction: CommentReaction = {
        emoji,
        userId,
        userName,
        createdAt: new Date(),
      };

      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await updateDoc(docRef, {
        reactions: [...reactions, newReaction],
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: commentId,
          changeType: 'update',
          changedFields: ['reactions'],
          reason: `Reaction added: ${emoji}`,
        },
        userId,
        userName,
        false
      );

    } catch (error: any) {
      console.error('Error adding reaction:', error);
      throw new Error(`Failed to add reaction: ${error.message}`);
    }
  }

  /**
   * Remove emoji reaction from a comment
   */
  static async removeReaction(
    commentId: string,
    emoji: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const existingComment = await this.getCommentById(commentId);
      if (!existingComment) {
        throw new Error('Comment not found');
      }

      const reactions = existingComment.reactions || [];
      const filteredReactions = reactions.filter(
        r => !(r.userId === userId && r.emoji === emoji)
      );

      if (reactions.length === filteredReactions.length) {
        throw new Error('Reaction not found');
      }

      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await updateDoc(docRef, {
        reactions: filteredReactions,
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: commentId,
          changeType: 'update',
          changedFields: ['reactions'],
          reason: `Reaction removed: ${emoji}`,
        },
        userId,
        userName,
        false
      );

    } catch (error: any) {
      console.error('Error removing reaction:', error);
      throw new Error(`Failed to remove reaction: ${error.message}`);
    }
  }

  /**
   * Soft delete a comment
   */
  static async deleteComment(
    commentId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const existingComment = await this.getCommentById(commentId);
      if (!existingComment) {
        throw new Error('Comment not found');
      }

      // Check permissions (owner or admin)
      const isAdmin = userRole === 'admin';
      const isOwner = existingComment.authorId === userId;

      if (!isAdmin && !isOwner) {
        throw new Error('You do not have permission to delete this comment');
      }

      // Soft delete
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await updateDoc(docRef, {
        isDeleted: true,
        deletedBy: userId,
        deletedAt: serverTimestamp(),
      });

      // Update reply count on parent if this was a reply
      if (existingComment.parentId) {
        await this.decrementReplyCount(existingComment.parentId);
      }

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'profileComment',
          entityId: commentId,
          changeType: 'delete',
          beforeState: existingComment,
          afterState: null,
          changedFields: ['isDeleted'],
          reason: 'Comment deleted',
        },
        userId,
        userName,
        isAdmin
      );

      // Log audit
      await AuditLogService.logAction({
        userId,
        action: 'comment_deleted',
        contentType: 'comment',
        contentId: commentId,
        details: {
          userName,
          personId: existingComment.personId,
          isReply: !!existingComment.parentId,
        },
      });

    } catch (error: any) {
      console.error('Error deleting comment:', error);
      throw new Error(`Failed to delete comment: ${error.message}`);
    }
  }

  // ========== PRIVATE HELPER METHODS ==========

  /**
   * Calculate thread depth by traversing up the parent chain
   */
  private static async calculateThreadDepth(commentId: string): Promise<number> {
    let depth = 1;
    let currentId = commentId;

    while (currentId) {
      const comment = await this.getCommentById(currentId);
      if (!comment || !comment.parentId) {
        break;
      }
      depth++;
      currentId = comment.parentId;
    }

    return depth;
  }

  /**
   * Increment reply count on parent comment
   */
  private static async incrementReplyCount(parentId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, parentId);
      await updateDoc(docRef, {
        replyCount: increment(1),
      });
    } catch (error: any) {
      console.error('Error incrementing reply count:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Decrement reply count on parent comment
   */
  private static async decrementReplyCount(parentId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, parentId);
      await updateDoc(docRef, {
        replyCount: increment(-1),
      });
    } catch (error: any) {
      console.error('Error decrementing reply count:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Check if user can edit comment based on time window
   */
  private static canEditComment(createdAt: any, authorId: string): boolean {
    // This will be checked in updateComment method
    // This is just for UI display purposes
    return true;
  }

  /**
   * Helper: Convert Firestore Timestamp to Date
   */
  private static timestampToDate(timestamp: any): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate) return timestamp.toDate();
    if (typeof timestamp === 'string') return new Date(timestamp);
    return new Date();
  }

  /**
   * Helper: Remove undefined fields from object (Firestore doesn't accept undefined)
   * Recursively cleans nested objects and arrays
   */
  private static removeUndefinedFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj
        .filter(item => item !== undefined)
        .map(item => this.removeUndefinedFields(item));
    }

    // Handle objects
    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const result: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          result[key] = this.removeUndefinedFields(obj[key]);
        }
      }
      return result;
    }

    // Return primitive values as-is
    return obj;
  }
}
